import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BarChart, Calculator, TrendingUp, Atom, BookOpen, Code } from "lucide-react";

const EducationHighSchool = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Random Number Generator for High Schools
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Advanced educational tools for teaching statistics, probability, computer science, and scientific research methods. 
            Support AP courses, STEM education, and college-prep mathematics.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <BarChart className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Statistical Analysis</h3>
              <p className="text-muted-foreground">
                Teach hypothesis testing, sampling distributions, confidence intervals, and statistical significance with real random data.
              </p>
            </Card>
            <Card className="p-6">
              <Code className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Computer Science</h3>
              <p className="text-muted-foreground">
                Demonstrate algorithms, cryptography, Monte Carlo methods, and the difference between PRNGs and true randomness.
              </p>
            </Card>
            <Card className="p-6">
              <Atom className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Scientific Method</h3>
              <p className="text-muted-foreground">
                Generate random samples for experiments, control groups, blind studies, and research requiring unbiased selection.
              </p>
            </Card>
          </div>
        </section>

        {/* Subject-Specific Applications */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">High School Subject Applications</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Calculator className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">AP Statistics & Data Science</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Sampling Distributions:</strong> Generate 1000+ random samples to demonstrate Central Limit Theorem</li>
                <li>• <strong>Hypothesis Testing:</strong> Create null distributions for p-value calculations and Type I/II errors</li>
                <li>• <strong>Random Sampling Methods:</strong> Practice simple random sampling, stratified sampling, cluster sampling</li>
                <li>• <strong>Simulation Studies:</strong> Monte Carlo simulations for probability problems (birthday paradox, Monty Hall)</li>
                <li>• <strong>Randomized Experiments:</strong> Assign subjects to control/treatment groups for experimental design projects</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Code className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Computer Science & Programming</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Algorithm Analysis:</strong> Compare computational efficiency of different random number algorithms</li>
                <li>• <strong>Cryptography Basics:</strong> Understand CSPRNG (cryptographically secure pseudo-random number generators)</li>
                <li>• <strong>Hash Functions:</strong> Explore randomness in hashing, collision detection, and load balancing</li>
                <li>• <strong>Game Development:</strong> Generate random events, loot drops, enemy behaviors, procedural content</li>
                <li>• <strong>Sorting Algorithm Demos:</strong> Generate random arrays to test quicksort, mergesort performance</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <TrendingUp className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">AP Calculus & Advanced Math</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Monte Carlo Integration:</strong> Estimate π, definite integrals, and area under curves using random sampling</li>
                <li>• <strong>Probability Distributions:</strong> Generate normal, uniform, exponential distributions for curve fitting</li>
                <li>• <strong>Limit Concepts:</strong> Demonstrate Law of Large Numbers with increasing sample sizes</li>
                <li>• <strong>Sequences & Series:</strong> Create random sequences to explore convergence and divergence</li>
                <li>• <strong>Optimization Problems:</strong> Use random search algorithms for local/global maxima</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Atom className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Science (Physics, Chemistry, Biology)</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Experimental Controls:</strong> Randomly assign treatment groups in biology experiments</li>
                <li>• <strong>Brownian Motion:</strong> Simulate random molecular movement in physics demonstrations</li>
                <li>• <strong>Radioactive Decay:</strong> Model probabilistic decay with random number generation</li>
                <li>• <strong>Genetic Crosses:</strong> Simulate Mendelian inheritance patterns and genetic drift</li>
                <li>• <strong>Survey Sampling:</strong> Create random samples for environmental studies and population ecology</li>
              </ul>
            </div>
          </div>
        </section>

        {/* AP Exam Preparation */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">AP Exam & SAT Preparation</h2>
          <div className="max-w-5xl mx-auto">
            <p className="text-muted-foreground text-center mb-8">
              Our cryptographically secure random number generator supports college readiness testing and standardized exam preparation:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">AP Statistics Exam Topics</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ Unit 3: Collecting Data (random sampling, experimental design)</li>
                  <li>✓ Unit 4: Probability (theoretical vs. experimental probability)</li>
                  <li>✓ Unit 5: Sampling Distributions (Central Limit Theorem demonstrations)</li>
                  <li>✓ Unit 7: Inference for Quantitative Data (randomization tests)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">SAT Math & ACT Science</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>✓ Data analysis and probability questions</li>
                  <li>✓ Experimental design and scientific method</li>
                  <li>✓ Graph interpretation with randomized data</li>
                  <li>✓ Statistical reasoning and inference</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Project Ideas */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">High School Research Project Ideas</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-3">Science Fair Projects</h3>
              <p className="text-muted-foreground mb-3">
                Investigate randomness quality, test hypothesis using random sampling, or explore probability paradoxes
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Testing true randomness vs pseudorandom</li>
                <li>• Birthday problem verification</li>
                <li>• Random walk simulations</li>
              </ul>
            </Card>
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-3">Coding Projects</h3>
              <p className="text-muted-foreground mb-3">
                Build random password generators, game simulators, or statistical analysis tools
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Lottery odds calculator</li>
                <li>• Dice game probability analyzer</li>
                <li>• Random quote generator</li>
              </ul>
            </Card>
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-3">Statistical Studies</h3>
              <p className="text-muted-foreground mb-3">
                Design surveys with random sampling, conduct experiments with randomized controls
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• School opinion polling</li>
                <li>• A/B testing for marketing</li>
                <li>• Clinical trial simulation</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Tools Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-6">Advanced Tools for Secondary Education</h2>
          <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <Link to="/">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <Calculator className="w-10 h-10 text-primary mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Number Generator</h4>
                <p className="text-sm text-muted-foreground">Any range, bulk generation</p>
              </Card>
            </Link>
            <Link to="/numbers">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <TrendingUp className="w-10 h-10 text-accent mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Gaussian Generator</h4>
                <p className="text-sm text-muted-foreground">Normal distributions</p>
              </Card>
            </Link>
            <Link to="/statistics">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <BarChart className="w-10 h-10 text-primary mx-auto mb-2" />
                <h4 className="font-semibold mb-1">Statistics Viewer</h4>
                <p className="text-sm text-muted-foreground">Analyze randomness quality</p>
              </Card>
            </Link>
            <Link to="/lists">
              <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full text-center">
                <BookOpen className="w-10 h-10 text-accent mx-auto mb-2" />
                <h4 className="font-semibold mb-1">List Randomizer</h4>
                <p className="text-sm text-muted-foreground">Shuffle & pick winners</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Free for All High School Students & Teachers</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            No registration, no limits. Support AP courses, science fairs, and college prep with cryptographically secure random numbers.
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Start Generating Random Numbers
            </Button>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default EducationHighSchool;